import { useEffect, useRef } from "react";
import { useAtom, useAtomValue } from "jotai";
import {
  campaignCodeAtom,
  campaignValidationAtom,
  selectedPackageAtom,
  selectedDateAtom,
} from "./store";
import { usePricingCampaignValidationMutation } from "../../api/campaign-hooks";
import { ValidateCampaignCommand, TimeOnly } from "@repo/rq-api-public";

export function useCampaignValidation() {
  const campaignCode = useAtomValue(campaignCodeAtom);
  const selectedPackage = useAtomValue(selectedPackageAtom);
  const selectedDate = useAtomValue(selectedDateAtom);
  const [, setCampaignValidation] = useAtom(campaignValidationAtom);

  const { mutate: validateCampaign } = usePricingCampaignValidationMutation();
  const hasValidatedCampaignRef = useRef<string | null>(null);

  useEffect(() => {
    // Early return if no campaign code - no need to do anything
    if (!campaignCode) {
      hasValidatedCampaignRef.current = null;
      // Reset validation state if we previously had a campaign code but now don't
      setCampaignValidation({
        isValid: null,
        isLoading: false,
      });
      return;
    }

    // Prevent running validation multiple times for the same campaign code
    if (hasValidatedCampaignRef.current === campaignCode) {
      return;
    }

    // Only validate when we have campaign code AND selected package with pricingGuid
    // Campaign codes only work with pricing packages, not calendar mode
    // This prevents unnecessary API calls on initial load
    if (selectedPackage && selectedPackage.pricingGuid) {
      hasValidatedCampaignRef.current = campaignCode;
      setCampaignValidation((prev) => ({
        ...prev,
        isLoading: true,
        error: undefined,
      }));

      const validationCommand = new ValidateCampaignCommand({
        campaignCode: campaignCode,
        customerGuid: "00000000-0000-0000-0000-000000000000", // Dummy GUID for general validation
        startDate: selectedDate,
        startTime: TimeOnly.fromJS(selectedPackage.startTime),
      });

      validateCampaign(
        {
          guid: selectedPackage.pricingGuid,
          body: validationCommand,
        },
        {
          onSuccess: (result) => {
            setCampaignValidation({
              isValid: true,
              isLoading: false,
              discountAmount: result.discountAmount,
            });
          },
          onError: (error) => {
            setCampaignValidation({
              isValid: false,
              isLoading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Campaign validation failed",
            });
          },
        },
      );
    } else if (selectedPackage && !selectedPackage.pricingGuid) {
      // Calendar mode - campaigns not supported
      hasValidatedCampaignRef.current = campaignCode;
      setCampaignValidation({
        isValid: false,
        isLoading: false,
        error: "Campaign codes are not available for calendar mode bookings",
      });
    }
  }, [campaignCode, selectedPackage, selectedDate]);
}
